import { createSlice, PayloadAction, Dispatch } from '@reduxjs/toolkit';
import { APIError } from 'models';
import {
  Acceptor,
  Address,
  Annotator,
  AuditConfig,
  AuditConfigSuccess,
  Authorizer,
  BasicData,
  BasicDataSuccess,
  CheckboxConfigType,
  ConfigDetailSuccess,
  ConfigFilter,
  ConfigSuccess,
  CreateServiceSuccess,
  Divert,
  HusqiotBusAttribute,
  HusqiotbusState,
  Service,
  SubscriptionSuccess,
  TemplatesSuccess,
  UpdateServiceSuccess,
  Validator,
  XmlFilesContent,
} from 'models/husqiotbus';
import { getDefaultForServiceType } from 'services/husqiotBusService/defaultConfigService';
import {
  readAuditConfig,
  readBasicData,
} from 'services/husqiotBusService/fileService';
import {
  parseStoredConfig,
  readConfigsFromFile,
  readTemplatesFromFiles,
  saveHusqiotBusConfig,
} from 'services/husqiotBusService/husqiotBusService';
import { getSubscriptions } from 'services/husqiotBusService/subscriptionService';
import type { RootState } from 'store/store';

const defaultService: Service = getDefaultForServiceType();

export const defaultState: HusqiotbusState = {
  config: {
    isLoading: false,
    isServiceLoading: false,
    isXmlLoading: false,
    error: null,
    selectedService: undefined,
    subscriptions: {
      data: [],
      isLoading: false,
      error: null,
    },
  },
  templates: [],
  basicData: {
    systems: [],
    annotators: [],
    roles: [],
    authorizers: [],
    validators: [],
    acceptors: [],
    serviceTypes: [],
    addresses: [],
  },
  auditConfig: {
    environments: [],
    excludedAdapters: [],
  },
  list: {
    data: [],
    totalCount: 0,
    isLoading: false,
    error: null,
    filters: {
      active: false,
      data: null,
    },
  },
};

// Export slice
export const husqiotbusSlice = createSlice({
  name: 'husqiotbus',
  initialState: defaultState,
  reducers: {
    getConfigs: (state) => {
      state.list.isLoading = true;
      state.list.error = null;
    },
    getConfigsSuccess: (state, action: PayloadAction<ConfigSuccess>) => {
      state.list.isLoading = false;
      state.list.error = null;
      state.list.data = action.payload.data;
      state.list.totalCount = action.payload.totalCount;
    },
    getConfigsFailure: (state, action: PayloadAction<APIError>) => {
      state.list.isLoading = false;
      state.list.error = action.payload.title;
    },
    getConfigDetails: (state) => {
      state.config.isLoading = true;
      state.config.error = null;
    },
    resetSelectedServiceState: (state) => {
      state.config.selectedService = undefined;
    },
    getConfigDetailsSuccess: (
      state,
      action: PayloadAction<ConfigDetailSuccess>,
    ) => {
      state.config.isLoading = false;
      state.config.error = null;
      const service = state.list.data.find(
        (config) => config.name === action.payload.name,
      );
      if (service) {
        state.config.selectedService = service;
      }
    },
    getConfigDetailsFailure: (state, action: PayloadAction<APIError>) => {
      state.config.isLoading = false;
      state.config.error = action.payload;
    },
    createService: (state) => {
      state.config.isLoading = true;
      state.config.error = null;
    },
    createServiceSuccess: (
      state,
      action: PayloadAction<CreateServiceSuccess>,
    ) => {
      state.config.isLoading = false;
      state.config.error = null;
      state.config.selectedService = {
        name: action.payload.serviceName,
        type: '',
        diamServiceName: '',
        annotators: [],
        authorizers: [],
        validators: [],
        acceptors: [],
        diverts: [],
        existingDiverts: [],
        addresses: [],
      };
    },
    updateService: (state) => {
      state.config.isXmlLoading = true;
      state.config.error = null;
    },
    updateServiceFailure: (state, action: PayloadAction<APIError>) => {
      state.config.isLoading = false;
      state.config.error = action.payload;
    },
    updateServiceType: (state) => {
      state.config.isServiceLoading = true;
      state.config.error = null;
    },
    updateServiceTypeSuccess: (
      state,
      action: PayloadAction<UpdateServiceSuccess>,
    ) => {
      state.config.isServiceLoading = false;
      state.config.error = null;
      const service: Service = state.config.selectedService || defaultService;
      if (action.payload.serviceType) {
        service.type = action.payload.serviceType;
        service.annotators = action.payload.annotators || [];
        service.acceptors = action.payload.acceptors || [];
        service.authorizers = action.payload.authorizers || [];
        service.validators = action.payload.validators || [];
        service.diverts = action.payload.diverts || [];
        service.addresses = action.payload.addresses || [];
      }
      if (action.payload.serviceName) {
        service.name = action.payload.serviceName;
      }
      if (action.payload.diamServiceName) {
        service.diamServiceName = action.payload.diamServiceName;
      }
      state.config.selectedService = service;
    },
    updateServiceDiverts: (state) => {
      state.config.isServiceLoading = true;
      state.config.error = null;
    },
    updateServiceDivertsSuccess: (state, action: PayloadAction<Divert>) => {
      state.config.isServiceLoading = false;
      state.config.error = null;
      const service: Service = state.config.selectedService || defaultService;
      service.diverts.push(action.payload);
      state.config.selectedService = service;
    },
    removeDivertSuccess: (state, action: PayloadAction<Divert>) => {
      state.config.isServiceLoading = false;
      state.config.error = null;
      const service: Service = state.config.selectedService || defaultService;
      service.diverts = service.diverts.filter(
        (divert) =>
          !(
            divert.serviceName === action.payload.serviceName &&
            divert.type === action.payload.type
          ),
      );
      state.config.selectedService = service;
    },
    updateServiceSuccess: (
      state,
      action: PayloadAction<UpdateServiceSuccess>,
    ) => {
      state.config.isXmlLoading = false;
      state.config.error = null;
      const service: Service = state.config.selectedService || defaultService;
      if (action.payload.annotators) {
        let currentCheckboxTypes: Annotator[] = service.annotators;
        action.payload.annotators.forEach((checkboxType) => {
          currentCheckboxTypes = getChecked(checkboxType, currentCheckboxTypes);
          service.annotators = currentCheckboxTypes;
        });
      }
      if (action.payload.validators) {
        let currentCheckboxTypes: Validator[] = service.validators;
        action.payload.validators.forEach((checkboxType) => {
          currentCheckboxTypes = getChecked(checkboxType, currentCheckboxTypes);
          service.validators = currentCheckboxTypes;
        });
      }
      if (action.payload.authorizers) {
        let currentCheckboxTypes: Authorizer[] = service.authorizers;
        action.payload.authorizers.forEach((checkboxType) => {
          currentCheckboxTypes = getChecked(checkboxType, currentCheckboxTypes);
          service.authorizers = currentCheckboxTypes;
        });
      }
      if (action.payload.acceptors) {
        let currentCheckboxTypes: Acceptor[] = service.acceptors;
        action.payload.acceptors.forEach((checkboxType) => {
          currentCheckboxTypes = getChecked(checkboxType, currentCheckboxTypes);
          service.acceptors = currentCheckboxTypes;
        });
      }
      if (action.payload.addresses) {
        let currentCheckboxTypes: Address[] = service.addresses;
        action.payload.addresses.forEach((checkboxType) => {
          currentCheckboxTypes = getChecked(checkboxType, currentCheckboxTypes);
          service.addresses = currentCheckboxTypes;
        });
      }
      state.config.selectedService = service;
    },
    saveConfig: (state, action: PayloadAction<boolean>) => {
      console.log('saveConfig action', action);
      state.config.isLoading = true;
    },
    saveConfigSuccess: (state, action: PayloadAction<boolean>) => {
      console.log('saveConfigSuccess action', action);
      state.config.isLoading = false;
      state.config.error = null;
    },
    saveConfigFailure: (state, action: PayloadAction<APIError>) => {
      state.config.isLoading = false;
      state.config.error = action.payload;
    },
    saveBasicData: (state) => {
      state.list.isLoading = true;
      state.list.error = null;
    },
    saveTemplatesSuccess: (state, action: PayloadAction<TemplatesSuccess>) => {
      state.list.isLoading = false;
      state.list.error = null;
      state.templates = action.payload.templates;
    },
    saveBasicDataSuccess: (state, action: PayloadAction<BasicDataSuccess>) => {
      state.list.isLoading = false;
      state.list.error = null;
      state.basicData = action.payload.basicData;
    },
    saveAuditConfigSuccess: (
      state,
      action: PayloadAction<AuditConfigSuccess>,
    ) => {
      state.list.isLoading = false;
      state.list.error = null;
      state.auditConfig = action.payload.auditConfig;
    },

    saveBasicDataFailure: (state, action: PayloadAction<APIError>) => {
      state.list.isLoading = false;
      state.list.error = action.payload.title;
    },
    getSubs: (state) => {
      state.config.subscriptions.isLoading = false;
      state.config.subscriptions.error = null;
    },
    getSubsSuccess: (state, action: PayloadAction<SubscriptionSuccess>) => {
      state.config.subscriptions.isLoading = false;
      state.config.subscriptions.error = null;
      state.config.subscriptions.data = action.payload.subscriptions;
    },

    getSubsFailure: (state, action: PayloadAction<APIError>) => {
      state.list.isLoading = false;
      state.list.error = action.payload.title;
    },
  },
});

// Export actions
export const {
  getConfigs,
  getConfigsSuccess,
  getConfigsFailure,
  getConfigDetails,
  getConfigDetailsSuccess,
  getConfigDetailsFailure,
  // getService,
  // getServiceSuccess,
  // getServiceFailure,
  createService,
  createServiceSuccess,
  updateService,
  updateServiceSuccess,
  updateServiceFailure,
  updateServiceType,
  updateServiceTypeSuccess,
  updateServiceDiverts,
  updateServiceDivertsSuccess,
  removeDivertSuccess,
  saveConfig,
  saveConfigSuccess,
  saveConfigFailure,
  saveBasicData,
  saveTemplatesSuccess,
  saveBasicDataSuccess,
  saveAuditConfigSuccess,
  saveBasicDataFailure,
  resetSelectedServiceState,
  getSubs,
  getSubsSuccess,
  getSubsFailure,
} = husqiotbusSlice.actions;

// Export reducer
export const husqiotbusReducer = husqiotbusSlice.reducer;

export const configListSelector = (state: RootState) => state.husqiotbus.list;

export const templateSelector = (state: RootState) =>
  state.husqiotbus.templates;

export const configSelector = (state: RootState) => state.husqiotbus.config;
export const basicDataSelector = (state: RootState) =>
  state.husqiotbus.basicData;

export const auditConfigSelector = (state: RootState) =>
  state.husqiotbus.auditConfig;

export const fetchTemplates = () => async (dispatch: Dispatch) => {
  dispatch(saveBasicData());
  try {
    const templates = await readTemplatesFromFiles();
    dispatch(saveTemplatesSuccess({ templates }));
  } catch (e: any) {
    dispatch(saveBasicDataFailure(e));
  }
};

export const fetchBasicData = () => async (dispatch: Dispatch) => {
  dispatch(saveBasicData());
  try {
    const basicData: BasicData = await readBasicData();
    dispatch(saveBasicDataSuccess({ basicData }));
  } catch (e: any) {
    dispatch(saveBasicDataFailure(e));
  }
};

export const fetchAuditConfig = () => async (dispatch: Dispatch) => {
  dispatch(saveBasicData());
  try {
    const auditConfig: AuditConfig = await readAuditConfig();
    dispatch(saveAuditConfigSuccess({ auditConfig }));
  } catch (e: any) {
    dispatch(saveBasicDataFailure(e));
  }
};

export const storeConfig =
  /* prettier-ignore */
  (name: string, diamServiceName: string, contactEmail: string, service: Service, xmlConfigurations?: XmlFilesContent) => async (dispatch: Dispatch) => {
    dispatch(saveConfig(true));
    try {
      const serviceToSave: Service = {
        name: name,
        diamServiceName,
        contactEmail,
        type: service.type,
        acceptors: service.acceptors,
        authorizers: service.authorizers,
        validators: service.validators,
        annotators: service.annotators,
        addresses: service.addresses,
        diverts: service.diverts,
        existingDiverts: service.existingDiverts,
        xml: xmlConfigurations,
      };
      await saveHusqiotBusConfig(serviceToSave);
      const configs = await readConfigsFromFile();
      const selectedConfigs = configs.slice(0, 10);
      const basicData = await readBasicData();
      const data = parseStoredConfig(selectedConfigs, basicData);
      dispatch(
        getConfigsSuccess({
          data,
          totalCount: configs.length,
        })
      );
    } catch (e: any) {
      dispatch(saveConfigFailure(e));
    }
  };

export const updateCheckedType =
  (name: HusqiotBusAttribute, value: any) => async (dispatch: Dispatch) => {
    dispatch(updateService());
    try {
      if (value.target.disabled) {
        // make sure we not change a value of checkboxes that are disabled
        return;
      }
      await timeout(500);
      const updateCheckboxList = [
        {
          name: value.target.value,
          checked: value.target.checked,
          disabled: false,
        },
      ];
      if (name === HusqiotBusAttribute.Annotators) {
        dispatch(
          updateServiceSuccess({
            annotators: updateCheckboxList,
          }),
        );
      }
      if (name === HusqiotBusAttribute.Acceptors) {
        dispatch(
          updateServiceSuccess({
            acceptors: updateCheckboxList,
          }),
        );
      }
      if (name === HusqiotBusAttribute.Validators) {
        dispatch(
          updateServiceSuccess({
            validators: updateCheckboxList,
          }),
        );
      }
      if (name === HusqiotBusAttribute.Authorizers) {
        dispatch(
          updateServiceSuccess({
            authorizers: updateCheckboxList,
          }),
        );
      }
      if (name === HusqiotBusAttribute.Addresses) {
        dispatch(
          updateServiceSuccess({
            addresses: updateCheckboxList,
          }),
        );
      }
    } catch (e: any) {
      dispatch(updateServiceFailure(e));
    }
  };

function timeout(delay: number) {
  return new Promise((res) => setTimeout(res, delay));
}
export const resetSelectedService = () => async (dispatch: Dispatch) => {
  dispatch(resetSelectedServiceState());
};
export const updateSelServiceFromType =
  /* prettier-ignore */
  (name: HusqiotBusAttribute, value: string, basicData: BasicData, serviceName?: string) => async (dispatch: Dispatch) => {
    dispatch(updateServiceType());
    try {
      if (name === HusqiotBusAttribute.ServiceType) {
        await timeout(1000); // Uggly fix to make page rerender to get checkboxes correct
        const service = getDefaultForServiceType(value, basicData, serviceName);
        dispatch(
          updateServiceTypeSuccess({
            serviceType: value,
            annotators: service.annotators,
            acceptors: service.acceptors,
            authorizers: service.authorizers,
            validators: service.validators,
            diverts: service.diverts,
            addresses: service.addresses,
          })
        );
      } else if (name === HusqiotBusAttribute.ServiceName) {
        dispatch(
          updateServiceTypeSuccess({
            serviceName: value,
          })
        );
      } else if (name === HusqiotBusAttribute.DiamServiceName) {
        dispatch(
          updateServiceTypeSuccess({
            diamServiceName: value,
          })
        );
      }
    } catch (e: any) {
      dispatch(updateServiceFailure(e));
    }
  };

export const addDivert = (divert: Divert) => async (dispatch: Dispatch) => {
  dispatch(updateServiceDiverts());
  try {
    dispatch(updateServiceDivertsSuccess(divert));
  } catch (e: any) {
    dispatch(updateServiceFailure(e));
  }
};

export const deleteDivert =
  (serviceName: string, type: string) => async (dispatch: Dispatch) => {
    dispatch(updateServiceDiverts());
    try {
      // await timeout(1000); // Uggly fix to make page rerender to get checkboxes correct
      dispatch(
        removeDivertSuccess({
          serviceName,
          type,
          filter: '',
          environments: [],
          version: '',
        }),
      );
    } catch (e: any) {
      dispatch(updateServiceFailure(e));
    }
  };

export const fetchConfigList =
  /* prettier-ignore */
  (offset: number, confFilter?: ConfigFilter) => async (dispatch: Dispatch) => {
    dispatch(getConfigs());
    try {
      const configs = await readConfigsFromFile();
      let selectedConfigs;
      if (confFilter?.attribute === 'name') {
        selectedConfigs = configs.filter((config) => config.name === `${confFilter.value}.json`)
      } else {
        selectedConfigs = configs.slice(offset, 10 + offset);
      }

      const basicData = await readBasicData();
      const data = parseStoredConfig(selectedConfigs, basicData);
      dispatch(
        getConfigsSuccess({
          data,
          totalCount: configs.length,
        })
      );
    } catch (e: any) {
      dispatch(getConfigsFailure(e));
    }
  };

export const fetchConfigDetails =
  (name: string) => async (dispatch: Dispatch) => {
    dispatch(getConfigDetails());
    try {
      dispatch(
        getConfigDetailsSuccess({
          name,
        }),
      );
    } catch (e: any) {
      dispatch(getConfigDetailsFailure(e));
    }
  };

export const fetchSubscriptions =  () => async (dispatch: Dispatch) => {
    dispatch(getSubs());
    try {
      const subscriptionResponse = await getSubscriptions();      
      dispatch(
        getSubsSuccess({subscriptions: subscriptionResponse.data?.map((sub) => {
          return sub?.attributes;

        } ).flat()})
      );
    } catch (e: any) {
      dispatch(getSubsFailure(e));
    }
  };

function getChecked(
  checkboxType: CheckboxConfigType,
  currentCheckboxTypes: CheckboxConfigType[],
) {
  return currentCheckboxTypes.map((c) => {
    if (c.name === checkboxType.name) {
      c.checked = checkboxType.checked;
      c.disabled = checkboxType.disabled;
    }
    return c;
  });
}
